import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, X, Shield, MapPin, User as UserIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  state_admin: 'State Admin',
  user: 'User',
};

const ROLE_BADGE = {
  super_admin: 'bg-saffron-100 text-saffron-800',
  state_admin: 'bg-gold-100 text-gold-800',
  user: 'bg-charcoal-100 text-charcoal-700',
  admin: 'bg-charcoal-100 text-charcoal-700',
};

const emptyForm = () => ({
  name: '',
  email: '',
  password: '',
  role: 'user',
  state_code: 'ALL',
  tab_permissions: {},
  is_active: true,
});

export const UserManagement = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [states, setStates] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [u, t, s, m] = await Promise.all([
        axios.get(`${API_URL}/api/rbac/users`, { withCredentials: true }),
        axios.get(`${API_URL}/api/rbac/tabs`, { withCredentials: true }),
        axios.get(`${API_URL}/api/rbac/states`, { withCredentials: true }),
        axios.get(`${API_URL}/api/rbac/me`, { withCredentials: true }),
      ]);
      setUsers(u.data);
      setTabs(t.data.tabs || []);
      setStates(s.data.states || []);
      setMe(m.data);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    const initial = emptyForm();
    // Default a state-admin's new user to their own state
    if (me?.role === 'state_admin') {
      initial.state_code = me.state_code;
      initial.role = 'user';
    }
    setForm(initial);
    setShowModal(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name,
      email: u.email,
      password: '',
      role: u.role,
      state_code: u.state_code,
      tab_permissions: u.tab_permissions || {},
      is_active: u.is_active,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const toggleTab = (tab) => {
    setForm((f) => ({
      ...f,
      tab_permissions: { ...f.tab_permissions, [tab]: f.tab_permissions[tab] ? 0 : 1 },
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      // Super admins always get full permissions server-side
      if (payload.role === 'super_admin') payload.tab_permissions = Object.fromEntries(tabs.map((t) => [t, 1]));
      if (editing) {
        if (!payload.password) delete payload.password;
        await axios.put(`${API_URL}/api/rbac/users/${editing.id}`, payload, { withCredentials: true });
        toast.success('User updated');
      } else {
        await axios.post(`${API_URL}/api/rbac/users`, payload, { withCredentials: true });
        toast.success('User created');
      }
      closeModal();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This revokes all their sessions.`)) return;
    try {
      await axios.delete(`${API_URL}/api/rbac/users/${u.id}`, { withCredentials: true });
      toast.success('User deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const stateName = (code) => states.find((s) => s.code === code)?.name || code;
  const isSuperAdmin = me?.role === 'super_admin';
  const availableRoles = isSuperAdmin ? ['super_admin', 'state_admin', 'user'] : ['state_admin', 'user'];
  // State admin can only grant tabs they themselves have
  const grantableTabs = tabs.filter((t) => {
    if (isSuperAdmin) return true;
    return me?.tab_permissions?.[t] === 1;
  });

  return (
    <div className="bg-white rounded-xl shadow-md border border-charcoal-100 p-6" data-testid="user-management">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-heading text-charcoal-800 flex items-center gap-2">
            <Shield className="text-saffron-600" size={22} /> Users & Permissions
          </h2>
          <p className="text-sm text-charcoal-500 mt-1">
            Manage Super Admins, State Admins, and Users. State Admins can only manage users within their assigned state.
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="user-add-button"
          className="flex items-center gap-2 bg-saffron-600 text-white rounded-lg px-4 py-2 hover:bg-saffron-500 transition-colors self-start sm:self-auto"
        >
          <Plus size={18} /> Add User
        </button>
      </div>

      {loading ? (
        <p className="text-center text-charcoal-500 py-12">Loading users...</p>
      ) : users.length === 0 ? (
        <p className="text-center text-charcoal-500 py-12">No users found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-charcoal-100">
                <th className="text-left py-3 px-4 font-body font-semibold text-charcoal-700">User</th>
                <th className="text-left py-3 px-4 font-body font-semibold text-charcoal-700">Role</th>
                <th className="text-left py-3 px-4 font-body font-semibold text-charcoal-700">State</th>
                <th className="text-left py-3 px-4 font-body font-semibold text-charcoal-700">Permissions</th>
                <th className="text-left py-3 px-4 font-body font-semibold text-charcoal-700">Status</th>
                <th className="text-right py-3 px-4 font-body font-semibold text-charcoal-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const grants = Object.entries(u.tab_permissions || {}).filter(([, v]) => v === 1);
                return (
                  <tr key={u.id} className="border-b border-charcoal-50 hover:bg-cream/40" data-testid={`user-row-${u.id}`}>
                    <td className="py-3 px-4">
                      <div className="font-medium text-charcoal-800">{u.name}</div>
                      <div className="text-xs text-charcoal-500">{u.email}</div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${ROLE_BADGE[u.role] || ROLE_BADGE.user}`}>
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-charcoal-700 flex items-center gap-1.5">
                      <MapPin size={14} className="text-charcoal-400" />
                      {stateName(u.state_code)}
                    </td>
                    <td className="py-3 px-4 text-xs text-charcoal-600">
                      {u.role === 'super_admin' ? (
                        <span className="text-saffron-700 font-medium">All access</span>
                      ) : grants.length === 0 ? (
                        <span className="text-charcoal-400">None</span>
                      ) : (
                        <span>{grants.length} of {tabs.length} tabs</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${u.is_active ? 'bg-green-100 text-green-800' : 'bg-charcoal-200 text-charcoal-600'}`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      <button
                        onClick={() => openEdit(u)}
                        data-testid={`user-edit-${u.id}`}
                        className="text-saffron-600 hover:text-saffron-500 mr-3"
                        title="Edit"
                      >
                        <Edit2 size={18} />
                      </button>
                      {u.id !== me?.id && (
                        <button
                          onClick={() => remove(u)}
                          data-testid={`user-delete-${u.id}`}
                          className="text-red-600 hover:text-red-500"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center p-6 border-b border-charcoal-100 sticky top-0 bg-white z-10">
              <h3 className="text-2xl font-heading text-charcoal-800 flex items-center gap-2">
                <UserIcon className="text-saffron-600" size={22} />
                {editing ? 'Edit User' : 'Add New User'}
              </h3>
              <button onClick={closeModal} className="text-charcoal-500 hover:text-charcoal-700" data-testid="user-modal-close">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Full Name *"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                  data-testid="user-form-name"
                />
                <input
                  type="email"
                  placeholder="Email *"
                  required
                  disabled={!!editing}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 disabled:bg-charcoal-50 disabled:text-charcoal-500"
                  data-testid="user-form-email"
                />
              </div>

              <input
                type="password"
                placeholder={editing ? 'New Password (leave blank to keep)' : 'Password *'}
                required={!editing}
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                data-testid="user-form-password"
              />

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-body font-semibold text-charcoal-600 mb-1.5 uppercase tracking-wider">Role</label>
                  <select
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                    data-testid="user-form-role"
                  >
                    {availableRoles.map((r) => (
                      <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-body font-semibold text-charcoal-600 mb-1.5 uppercase tracking-wider">State</label>
                  <select
                    value={form.state_code}
                    disabled={!isSuperAdmin}
                    onChange={(e) => setForm({ ...form, state_code: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 disabled:bg-charcoal-50"
                    data-testid="user-form-state"
                  >
                    {states.map((s) => (
                      <option key={s.code} value={s.code}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {form.role !== 'super_admin' && (
                <div className="bg-cream/50 border border-charcoal-100 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="text-sm font-body font-semibold text-charcoal-700">Tab Permissions</div>
                      <div className="text-[11px] text-charcoal-500 mt-0.5">Check each admin tab this user is allowed to open.</div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, tab_permissions: Object.fromEntries(grantableTabs.map((t) => [t, 1])) })}
                        className="text-xs text-saffron-700 hover:text-saffron-600 font-semibold"
                        data-testid="user-perms-all"
                      >
                        Grant all
                      </button>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, tab_permissions: {} })}
                        className="text-xs text-charcoal-500 hover:text-charcoal-700 font-semibold"
                        data-testid="user-perms-none"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {tabs.map((tab) => {
                      const isGrantable = grantableTabs.includes(tab);
                      const checked = form.tab_permissions[tab] === 1;
                      return (
                        <label
                          key={tab}
                          className={`flex items-center gap-2 rounded-lg border p-2.5 text-sm cursor-pointer transition-colors ${
                            checked
                              ? 'bg-saffron-50 border-saffron-300 text-saffron-800'
                              : 'bg-white border-charcoal-200 text-charcoal-700 hover:border-saffron-200'
                          } ${!isGrantable ? 'opacity-40 cursor-not-allowed' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!isGrantable}
                            onChange={() => toggleTab(tab)}
                            className="accent-saffron-600"
                            data-testid={`user-perm-${tab}`}
                          />
                          <span className="capitalize">{tab.replace(/_/g, ' ')}</span>
                        </label>
                      );
                    })}
                  </div>
                  {!isSuperAdmin && (
                    <p className="text-[11px] text-charcoal-500 italic mt-3">
                      As a State Admin, you can only grant permissions you yourself hold. Grey-out means not grantable.
                    </p>
                  )}
                </div>
              )}

              {editing && (
                <label className="flex items-center gap-2 text-sm text-charcoal-700">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    data-testid="user-form-active"
                  />
                  <span>Account active (unchecking revokes all sessions immediately)</span>
                </label>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 border border-charcoal-200 text-charcoal-700 rounded-lg px-6 py-3 hover:bg-charcoal-50 transition-colors"
                  data-testid="user-form-cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-saffron-600 text-white rounded-lg px-6 py-3 hover:bg-saffron-500 transition-colors"
                  data-testid="user-form-submit"
                >
                  {editing ? 'Save Changes' : 'Create User'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};
